import { useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useDisplayUserError } from '@yes.technology/react-toolkit'
import { Step, InteractionModel } from 'types/shared'
import * as googleTagManager from 'modules/shared/GTM'

import { validateInteractionModelCallback } from '../../../../utils/interactionModel'
import { getStepErrorMessages, showError } from '../../utils/validation'
import useAutomation from '../useAutomation'
import { fetchWorkflow } from '../useWorkflow'
import useValidateStep from '../useValidateStep/useValidateStep'
import { useSearchParams } from 'react-router-dom'
import useSaveInteraction from 'utils/useSaveInteraction'
import { DefaultRootState, useStore } from 'react-redux'
import { useAppDispatch } from 'state/store'
import { startLoading, stopLoading } from 'modules/interaction/redux'

const DEFAULT_AUTOMATION_DELAY = 6000

const EMPTY_OBJECT = {}

const useSaveAndFinish = (
  interactionModel: InteractionModel,
  onSave: () => void,
  visibleSteps: Step[]
) => {
  const { t } = useTranslation()

  const [uuidInteraction, setUuidInteraction] = useState(null)
  const [isInteractionFinished, setIsInteractionFinished] = useState(false)

  const activityModels =
    interactionModel.flatData?.activity_models || EMPTY_OBJECT
  const activityValues =
    interactionModel.flatData?.activity_values || EMPTY_OBJECT

  const [isExecutingAutomations, setIsExecutingAutomations] = useState(false)
  const executeAutomation = useAutomation()

  const executeAutomationsAfterSuccess = async () => {
    setIsExecutingAutomations(true)

    const automationDelay =
      interactionModel?.flatData?.automation_delay || DEFAULT_AUTOMATION_DELAY

    await new Promise((r) => setTimeout(r, automationDelay))

    const automations = interactionModel.flatData?.automations || []

    const sortedAutomations = automations
      .slice()
      .sort((firstAutomation, secondAutomation) => {
        if (firstAutomation.automation_type === 'redirect') return 1
        if (secondAutomation.automation_type === 'redirect') return -1
        return 0
      })

    const workflow = await fetchWorkflow(interactionModel.workflowUuid, t).then(
      (response) => response.data?.token
    )

    for (const automation of sortedAutomations) {
      if (automation.trigger === 'submit-success') {
        await executeAutomation(automation, workflow)
      }
    }

    setIsExecutingAutomations(false)
  }

  const { validateStep } = useValidateStep()
  const { displayUserError } = useDisplayUserError()

  const validateInteraction = async (): Promise<boolean> => {
    const stepValidations = await Promise.all(
      visibleSteps.map(async (flatStep) => {
        const { invalidActivities } = await validateStep({
          activityModels: activityModels,
          activityValues: activityValues,
          stepActivities: flatStep.activity_models
        })

        return {
          invalidActivities,
          stepName: flatStep.des
        }
      })
    )

    stepValidations.forEach(({ invalidActivities, stepName }) =>
      getStepErrorMessages({
        invalidActivities,
        stepName,
        t
      }).forEach((error) => showError({ error }))
    )

    if (
      stepValidations.some(
        ({ invalidActivities }) => invalidActivities.length > 0
      )
    ) {
      return false
    }

    const { flatData } = interactionModel

    if (flatData?.validation_callback) {
      const responseValidationCallback = await validateInteractionModelCallback(
        flatData,
        flatData.validation_callback
      )

      if (!responseValidationCallback?.success) {
        displayUserError({ error: responseValidationCallback })

        return false
      }
    }

    return true
  }

  const { updateInteraction } = useSaveInteraction()
  const [searchParams, setSearchParams] = useSearchParams()
  const draftInteractionUuid = searchParams.get('draft_interaction_uuid') || ''

  const dispatch = useAppDispatch()

  const saveAndFinish = async (updatedInteractionModel: InteractionModel) => {
    const saveStartTimestamp = performance.now()

    const response = await updateInteraction({
      shownSteps: visibleSteps,
      interactionModel: updatedInteractionModel,
      draftUuid: draftInteractionUuid,
      activate: true
    }).catch((error) =>
      error.json().then((body: { message?: string }) => {
        dispatch(stopLoading())

        return {
          success: false,
          status: error?.status,
          message: body?.message || 'Failed Request'
        }
      })
    )

    const saveEndTimestamp = performance.now()
    const executionTime = saveEndTimestamp - saveStartTimestamp
    const loadingDelay =
      (updatedInteractionModel.flatData?.review?.save_loading?.delay || 0) -
      executionTime

    if (loadingDelay > 0) {
      await new Promise((r) => setTimeout(r, loadingDelay))
    }

    dispatch(stopLoading())

    if (!response.success && response.status !== 503) {
      displayUserError({
        title: t('step.save.failure-title'),
        userMessageFallback: t('step.save.failure-message'),
        error: response
      })

      console.error(response.message)

      return false
    }

    const newUuidInteraction = response?.data?.new_interaction_ids?.[0]

    setUuidInteraction(draftInteractionUuid || newUuidInteraction)
    executeAutomationsAfterSuccess()
    onSave?.()
    setIsInteractionFinished(true)

    setSearchParams(
      (oldSearchParams) => {
        const newParams = new URLSearchParams(oldSearchParams)
        newParams.delete('draft_interaction_uuid')
        return newParams
      },
      { replace: true }
    )

    // Send interaction_complete event to Google Tag Manager
    googleTagManager.setDataLayer({
      event: googleTagManager.Events.INTERACTION_COMPLETE
    })

    return true
  }

  const store = useStore<DefaultRootState>()

  const startSaveLoading = () => {
    const saveLoading = interactionModel.flatData?.review?.save_loading || {}

    dispatch(
      startLoading({
        des: saveLoading.des || t('step.review.save-loading.des'),
        complement:
          saveLoading.complement || t('step.review.save-loading.complement')
      })
    )
  }

  const saveAndFinishAfterDraftUpdates = async () => {
    const isInteractionValid = await validateInteraction()

    if (!isInteractionValid) return

    startSaveLoading()

    let updatedState = interactionModel

    if (updatedState.isUpdatingDraft) {
      const checkInterval = 200

      await new Promise((resolve) => {
        const intervalId = setInterval(() => {
          updatedState = store.getState().interactionModel
          const currentIsUpdatingDraft = updatedState.isUpdatingDraft

          if (!currentIsUpdatingDraft) {
            clearInterval(intervalId)
            resolve(true)
          }
        }, checkInterval)
      })
    }

    await saveAndFinish(updatedState)

    dispatch(stopLoading())
  }

  return {
    saveAndFinishAfterDraftUpdates,
    isExecutingAutomations,
    uuidInteraction,
    isInteractionFinished
  }
}

export default useSaveAndFinish
